import { Button, Collapse, TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ObjectSchema } from 'yup';
import { useAtomValue } from 'jotai/index';
import { debounce } from 'lodash-es';
import { ChangeEvent, FocusEvent, useCallback, useEffect, useState } from 'react';
import { FieldGroup } from '../../../../components/FieldGroup/FieldGroup';
import { FormField } from '../../../../components/FormField/FormField';
import { useModalState } from '../../../../components/Modal/ModalContext';
import { MuiSingleSelect } from '../../../../components/MuiSingleSelect/MuiSingleSelect';
import { createCompanyDataModel } from '../../../../data-models/company.data-model';
import { ICreateListItemForm, initCreateListItemForm } from '../../../../data-models/list-item.data-model';
import { useCreateCompanyAndUpdateState } from '../../../../services/hooks/useCreateCompanyAndUpdateState';
import { useListUploadActions } from '../../../../services/hooks/useListActions';
import { activeUsersAtom } from '../../../../services/state/AppConfigStateJ';
import { companyToSearchResultWithRealId } from '../../../../util/companyUtils';
import { CompanySuggestion } from '../../../DealFlow2/components/DealModal/CompanyForm/CompanySuggestion';
import { useDuplicateWebsiteHandling } from '../../../DealFlow2/components/DealModal/CompanyForm/useDuplicateWebsiteHandling';
import { DealModal } from '../../../DealFlow2/components/DealModal/DealModal';
import { FormWrapper } from '../../../Finance/components/TransactionModal/Forms/TransactionForms/Equity/EquityBuyForm';
import { SectorSelect } from '../../../Finance/form-components/SectorSelect';
import { activeCompanyListIdStateJ, listsByIdStateJ } from '../../../../services/state/ListsStateJ';
import { CreateListItemSchema as validationSchema } from './CreateListItemSchema';

const DuplicateMessage = 'Company already exists';

const ButtonWrapper = styled('div')`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  padding-bottom: 2rem;
`;

export const CreateListItemForm = ({ name }: { name: string }) => {
  const users = useAtomValue(activeUsersAtom);
  const mappedUsers = users.map(({ id, name }) => ({ id, value: name }));
  const { onCloseModal } = useModalState();

  const activeListId = useAtomValue(activeCompanyListIdStateJ);
  const listsMap = useAtomValue(listsByIdStateJ);
  const activeList = activeListId != null ? listsMap.get(activeListId) : null;
  const createCompanyAndUpdateState = useCreateCompanyAndUpdateState(false);
  const { addCompanyToList } = useListUploadActions();

  const [touched, setTouched] = useState<Partial<Record<keyof ICreateListItemForm, boolean>>>({});

  const {
    watch,
    setValue,
    setError,
    clearErrors,
    trigger,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ICreateListItemForm>({
    defaultValues: initCreateListItemForm({ name }),
    mode: 'all',
    resolver: yupResolver(validationSchema as ObjectSchema<ICreateListItemForm>),
  });

  const values = watch();

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValue(e.target.name as keyof ICreateListItemForm, e.target.value as never, {
        shouldValidate: true,
      });
    },
    [setValue]
  );

  const handleBlur = useCallback((e: FocusEvent<HTMLElement>) => {
    const { name: fieldName } = e.target as HTMLInputElement;
    if (fieldName) {
      setTouched((prev) => ({ ...prev, [fieldName]: true }));
    }
  }, []);

  const { suggestedCompany, checkForDuplicates, resetSuggestions } = useDuplicateWebsiteHandling();

  const handleCreate = useCallback(
    async (values: ICreateListItemForm) => {
      if (!activeList) {
        return;
      }
      const company = suggestedCompany ?? (await createCompanyAndUpdateState(values));
      if (!company) {
        return;
      }
      await addCompanyToList(activeList, companyToSearchResultWithRealId(company));
      onCloseModal();
    },
    [activeList, addCompanyToList, createCompanyAndUpdateState, onCloseModal, suggestedCompany]
  );

  const handleCancelSuggestion = useCallback(() => {
    clearErrors('website');
    setValue('website', '');
    resetSuggestions();
  }, [resetSuggestions, clearErrors, setValue]);

  const handleSelectSuggestions = useCallback(() => {
    if (!suggestedCompany) {
      return;
    }
    setValue('name', suggestedCompany.name);
    setValue('website', suggestedCompany.website ?? '');
    setValue('sectorId', suggestedCompany.sectorId != null ? Number(suggestedCompany.sectorId) : null);
    clearErrors('website');
  }, [clearErrors, setValue, suggestedCompany]);

  useEffect(() => {
    const debouncedCheck = debounce((url) => {
      checkForDuplicates(url);
    }, 300);

    if (values.website) {
      debouncedCheck(values.website);
    } else {
      clearErrors('website');
      resetSuggestions();
    }
  }, [checkForDuplicates, resetSuggestions, clearErrors, values.website]);

  const isDuplicate = !!suggestedCompany && suggestedCompany.name !== values.name;

  useEffect(() => {
    if (isDuplicate) {
      setError('website', { message: DuplicateMessage });
    } else if (values.website) {
      trigger('website');
    }
  }, [isDuplicate, setError, trigger, values.website]);

  const handleError = (field: keyof ICreateListItemForm) => {
    const message = errors[field as keyof typeof errors]?.message;
    return touched[field] && typeof message === 'string' ? message : '';
  };

  return (
    <DealModal title={`Create new company with name '${name}'`} width={'814px'} contentPadding={'0 40px'}>
      <FormWrapper onSubmit={handleSubmit((vals) => handleCreate(vals))}>
        <FieldGroup hasBorder={false}>
          <FormField label={'Company Name'} required error={handleError('name')}>
            <TextField
              name={'name'}
              value={values.name}
              onChange={handleChange}
              onBlur={handleBlur}
              variant='outlined'
              placeholder='Enter a company name'
            />
          </FormField>
          <FormField label={'Website'} error={errors.website?.message}>
            <TextField
              name={'website'}
              value={values.website}
              onChange={handleChange}
              variant='outlined'
              placeholder='Enter a website'
            />
          </FormField>
          <Collapse in={isDuplicate}>
            <CompanySuggestion
              company={suggestedCompany ?? createCompanyDataModel()}
              onCancel={handleCancelSuggestion}
              onSelect={handleSelectSuggestions}
              style={{ marginBottom: '1rem' }}
            />
          </Collapse>

          <FormField label={'Sector'}>
            <SectorSelect
              onChange={(sectorId) => setValue('sectorId', sectorId, { shouldValidate: true })}
              value={values.sectorId}
              onFocus={() => setTouched((prev) => ({ ...prev, sectorId: true }))}
            />
          </FormField>
        </FieldGroup>
        <FieldGroup>
          <FormField label={'Assignee'} error={handleError('assignee')}>
            <MuiSingleSelect
              options={mappedUsers ?? []}
              disablePortal
              fieldName={'assignee'}
              value={mappedUsers?.find((u) => u.id === values.assignee) ?? null}
              onChange={(_, option) =>
                setValue('assignee', option?.id != null ? Number(option.id) : null, { shouldValidate: true })
              }
              onFocus={() => setTouched((prev) => ({ ...prev, assignee: true }))}
              fieldPlaceholder='Select a user'
            />
          </FormField>
        </FieldGroup>
        <ButtonWrapper>
          <Button variant={'contained'} color='secondary' disabled={!isValid || isDuplicate} type={'submit'}>
            Add company
          </Button>
          <Button variant={'outlined'} color='secondary' onClick={onCloseModal}>
            Cancel
          </Button>
        </ButtonWrapper>
      </FormWrapper>
    </DealModal>
  );
};
